import { Injectable } from '@angular/core';

import countriesJson from './../../../assets/data/countries.json';
import statesJson from './../../../assets/data/states.json';
import countriesOptionalJson from './../../../assets/data/countriesOptional.json';
import countrySettingsJson from './../../../assets/data/countrySettings.json';

export interface SimpleNamedData {
  id: string;
  name: string;
}

export interface CountrySettingsData {
  id: string;
  zipPattern: string;
  zipMask: {
    guide: boolean;
    mask: Array<string>;
  };
  phonePrefix: string;
  phonePattern: string;
  phoneMask: {
    guide: boolean;
    mask: Array<string>;
  };
}

export interface CountrySettings {
  id: string;
  zipPattern: RegExp;
  zipMask: {
    guide: boolean;
    mask: Array<any>;
  };
  phonePrefix: string;
  phonePattern: RegExp;
  phoneMask: {
    guide: boolean;
    mask: Array<any>;
  };
}

export interface CountrySettingsJson {
  default: CountrySettingsData;
  countries: Array<CountrySettingsData>;
}

export const DEFAULT = 'default';

@Injectable({
  providedIn: 'root'
})
export class CountryService {

  private countriesFormatted: Array<SimpleNamedData>;
  private countriesOptionalFormatted: Array<string>;
  private countrySettingsFormatted: any;
  private statesFormatted: any;
  private allowedMaskChars: Array<string> = [' ', '-', '(', ')'];

  constructor() {

    this.countriesFormatted =
      <Array<SimpleNamedData>>(<Object>countriesJson);
    this.statesFormatted = <Object>statesJson;
    this.countriesOptionalFormatted =
      <Array<string>>(<Object>countriesOptionalJson);

    this.countrySettingsFormatted =
      this.formatCountrySettings(countrySettingsJson as unknown as CountrySettingsJson);
  }

  private formatCountrySettings(
    settings: CountrySettingsJson
  ): Array<CountrySettings> {

    const defaultSettings = settings.default;
    const arrayOfSettings = [];

    Object.keys(settings.countries)
      .forEach(key => {
        arrayOfSettings[key] =
          this.createCountrySettings(defaultSettings, settings.countries[key]);
      });

    arrayOfSettings[DEFAULT] =
      this.createCountrySettings(defaultSettings, defaultSettings);

    return arrayOfSettings;
  }

  private createCountrySettings(
    defaultSettings: any,
    settings: CountrySettingsData
  ) {

    const countrySettingsData = {...defaultSettings, ...settings};

    const countrySettings = <CountrySettings>{
      id: countrySettingsData.id,
      zipPattern: new RegExp(countrySettingsData.zipPattern),
      zipMask: {
        guide: countrySettingsData.zipMask.guide,
        mask: this.arrayToMask(countrySettingsData.zipMask.mask)
      },
      phonePrefix: countrySettingsData.phonePrefix,
      phonePattern: new RegExp(countrySettingsData.phonePattern),
      phoneMask: {
        guide: countrySettingsData.phoneMask.guide,
        mask: this.arrayToMask(countrySettingsData.phoneMask.mask)
      }
    };

    return countrySettings;
  }

  private arrayToMask(array: Array<string>): Array<any> {
    return array.map(item => {
      return (item.length === 1 && this.allowedMaskChars.includes(item))
        ? item
        : new RegExp(item);
    });
  }

  public get countries(): Array<SimpleNamedData> {
    return this.countriesFormatted;
  }

  public get states(): any {
    return this.statesFormatted;
  }

  public get countrySettings(): any {
    return this.countrySettingsFormatted;
  }

  // wrapper on countrySettings to provide default settings instead nothing
  public getCountryInfo(country): any {
    return this.countrySettings[country]
      || this.countrySettingsDefault;
  }

  public get countrySettingsDefault(): any {
    return this.countrySettingsFormatted[DEFAULT];
  }

  public isZipOptional(countryCode: string): boolean {
    return this.countriesOptionalFormatted
      .findIndex(e => e === countryCode) > -1;
  }
}
